
import React from "react";
import { Toaster as SonnerToaster, toast } from "@/components/ui/sonner";
import { ThemeProvider } from "next-themes";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Layout } from "./components/layout/layout";
import Index from "./pages/index";
import Login from "./pages/Login";
import NotFound from "./pages/NotFound";
import AuthCallback from "./pages/auth/AuthCallback";
import VanityUrlRedirect from "./pages/VanityUrlRedirect";
import { getVersionInfo } from "./utils/app-version";
import { lazyWithRetry } from "./utils/lazy-with-retry";
import { RouteErrorBoundary } from "./components/ui/route-error-boundary";
import { RsvpSkeleton } from "./components/rsvp/rsvp-skeleton";
import "./App.css";

// Lazy-load heavy pages
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const CreatePoll = lazyWithRetry(() => import("./pages/CreatePoll"));
const Teams = lazyWithRetry(() => import("./pages/Teams"));
const TeamDetail = lazyWithRetry(() => import("./pages/TeamDetail"));
const GameVote = lazyWithRetry(() => import("./pages/GameVote"));
const GameRsvp = lazyWithRetry(() => import("./pages/GameRsvp"));
const Stats = lazyWithRetry(() => import("./pages/Stats"));
const SeasonWrap = lazyWithRetry(() => import("./pages/SeasonWrap"));
const Settings = lazyWithRetry(() => import("@/pages/Settings"));
const PlayerContacts = lazyWithRetry(() => import("@/pages/PlayerContacts"));
const RpeSessions = lazyWithRetry(() => import("@/pages/RpeSessions"));
const RpeNewSession = lazyWithRetry(() => import("@/pages/RpeNewSession"));
const RpeSessionDetailPage = lazyWithRetry(() => import("@/pages/RpeSessionDetail"));
const RpeResponse = lazyWithRetry(() => import("@/pages/RpeResponse"));
const RpeAnalyticsPage = lazyWithRetry(() => import("@/pages/RpeAnalytics"));
const Docs = lazyWithRetry(() => import("@/pages/Docs"));
const Terms = lazyWithRetry(() => import("@/pages/Terms"));
const CreditManagementPage = lazyWithRetry(() => import("@/pages/CreditManagement"));
const TrainingAttendance = lazyWithRetry(() => import("@/pages/TrainingAttendance"));
const TrainingRollCall = lazyWithRetry(() => import("@/pages/TrainingRollCall"));
const TrainingSessionDetail = lazyWithRetry(() => import("@/pages/TrainingSessionDetail"));
const MatchAttendanceDetail = lazyWithRetry(() => import("@/pages/MatchAttendanceDetail"));
const TeamSettings = lazyWithRetry(() => import("@/pages/TeamSettings"));
const ParentDashboard = lazyWithRetry(() => import("@/pages/ParentDashboard"));
const CoparentRequest = lazyWithRetry(() => import("@/pages/parent/CoparentRequest"));

const Admin = lazyWithRetry(() => import("@/pages/Admin"));
const RsvpInfo = lazyWithRetry(() => import("@/pages/RsvpInfo"));
const RpeInfo = lazyWithRetry(() => import("@/pages/RpeInfo"));
const FeedbackInfo = lazyWithRetry(() => import("@/pages/FeedbackInfo"));
const FixturesInfo = lazyWithRetry(() => import("@/pages/FixturesInfo"));
const AttendanceInfo = lazyWithRetry(() => import("@/pages/AttendanceInfo"));
const ImportFixtures = lazyWithRetry(() => import("@/pages/ImportFixtures"));
const AutoSync = lazyWithRetry(() => import("@/pages/AutoSync"));
const CoachLog = lazyWithRetry(() => import("@/pages/CoachLog"));
const OgHealth = lazyWithRetry(() => import("@/pages/OgHealth"));
const Gallery = lazyWithRetry(() => import("@/pages/Gallery"));
const CssCheck = lazyWithRetry(() => import("@/pages/CssCheck"));

import { DefaultDashboardRedirect } from "@/components/dashboard/default-dashboard-redirect";
import { PulsePageview } from "@/components/analytics/pulse-pageview";
import { ViewerAccentBar } from "@/components/layout/viewer-accent-bar";

// Clear only the inline props set by the anti-FOUC script in index.html.
// A blanket removeAttribute('style') fights any inline styles Tailwind or
// other setup code may have applied and triggers an extra style recalc.
try {
  document.documentElement.style.removeProperty('background-color');
  document.documentElement.style.removeProperty('color-scheme');
} catch { /* noop */ }


// Make toast available for service worker updates
window.showToast = ({ title, description, action }) => {
  toast(title, {
    description,
    duration: 0 // Don't auto-dismiss
  });
};

// Log version info for debugging
console.log("App starting with version:", getVersionInfo());

// Hoist QueryClient to module scope to preserve cache across re-renders
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,        // 1 minute
      gcTime: 5 * 60 * 1000,       // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const PageSuspense = ({ children }: { children: React.ReactNode }) => (
  <React.Suspense fallback={
    // Solid background + visible spinner + label — prevents the dark <body>
    // from making this look like a totally black, broken page during chunk load.
    <div className="flex flex-col items-center justify-center min-h-[60vh] bg-background gap-3">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-muted border-t-primary" />
      <p className="text-sm text-muted-foreground">Loading…</p>
    </div>
  }>
    {children}
  </React.Suspense>
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <TooltipProvider>
          <BrowserRouter>
            <SonnerToaster />
            <PulsePageview />
            <ViewerAccentBar />
            <Layout>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/login" element={<Login />} />
                <Route path="/teams" element={<PageSuspense><Teams /></PageSuspense>} />
                <Route path="/teams/:teamId" element={<PageSuspense><TeamDetail /></PageSuspense>} />
                <Route path="/teams/:teamId/dashboard" element={<PageSuspense><Dashboard /></PageSuspense>} />
                <Route path="/dashboard" element={<DefaultDashboardRedirect />} />
                <Route path="/create" element={<PageSuspense><CreatePoll /></PageSuspense>} />
                <Route path="/game/:id" element={<RouteErrorBoundary><PageSuspense><GameVote /></PageSuspense></RouteErrorBoundary>} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/rsvp/:gameId" element={<RouteErrorBoundary><React.Suspense fallback={<RsvpSkeleton />}><GameRsvp /></React.Suspense></RouteErrorBoundary>} />
                <Route path="/stats" element={<PageSuspense><Stats /></PageSuspense>} />
                
                {/* Primary route format - direct team lookup by slug */}
                <Route path="/t/:urlSlug" element={<VanityUrlRedirect />} />
                
                {/* Legacy formats - keep for backward compatibility */}
                <Route path="/poll/team/:teamName" element={<VanityUrlRedirect />} />
                <Route path="/poll/:clubAcronym/:teamName" element={<VanityUrlRedirect />} />
                <Route path="/poll/:urlSlug/team/:teamName" element={<VanityUrlRedirect />} />
                <Route path="/poll/:urlSlug/:clubAcronym/:teamName" element={<VanityUrlRedirect />} />
                
                <Route path="/settings" element={<PageSuspense><Settings /></PageSuspense>} />
                <Route path="/coach-log" element={<PageSuspense><CoachLog /></PageSuspense>} />
                <Route path="/admin/og-health" element={<PageSuspense><OgHealth /></PageSuspense>} />
                <Route path="/admin/gallery" element={<PageSuspense><Gallery /></PageSuspense>} />
                {import.meta.env.DEV && (
                  <Route path="/dev/css-check" element={<PageSuspense><CssCheck /></PageSuspense>} />
                )}
                <Route path="/teams/:teamId/wrap" element={<PageSuspense><SeasonWrap /></PageSuspense>} />
                <Route path="/teams/:teamId/settings" element={<PageSuspense><TeamSettings /></PageSuspense>} />
                <Route path="/teams/:teamId/contacts" element={<PageSuspense><PlayerContacts /></PageSuspense>} />
                <Route path="/teams/:teamId/rpe" element={<PageSuspense><RpeSessions /></PageSuspense>} />
                <Route path="/teams/:teamId/rpe/analytics" element={<PageSuspense><RpeAnalyticsPage /></PageSuspense>} />
                <Route path="/teams/:teamId/rpe/credits" element={<PageSuspense><CreditManagementPage /></PageSuspense>} />
                <Route path="/teams/:teamId/rpe/new" element={<PageSuspense><RpeNewSession /></PageSuspense>} />
                <Route path="/teams/:teamId/rpe/:sessionId" element={<PageSuspense><RpeSessionDetailPage /></PageSuspense>} />
                <Route path="/rpe/:token" element={<RouteErrorBoundary><PageSuspense><RpeResponse /></PageSuspense></RouteErrorBoundary>} />
                <Route path="/teams/:teamId/attendance" element={<PageSuspense><TrainingAttendance /></PageSuspense>} />
                <Route path="/teams/:teamId/attendance/new" element={<PageSuspense><TrainingRollCall /></PageSuspense>} />
                <Route path="/teams/:teamId/attendance/match/:gameId" element={<PageSuspense><MatchAttendanceDetail /></PageSuspense>} />
                <Route path="/teams/:teamId/attendance/:sessionId" element={<PageSuspense><TrainingSessionDetail /></PageSuspense>} />
                <Route path="/teams/:teamId/auto-sync" element={<PageSuspense><AutoSync /></PageSuspense>} />
                <Route path="/docs" element={<PageSuspense><Docs /></PageSuspense>} />
                <Route path="/terms" element={<PageSuspense><Terms /></PageSuspense>} />
                <Route path="/parent" element={<RouteErrorBoundary><PageSuspense><ParentDashboard /></PageSuspense></RouteErrorBoundary>} />
                {/* Coach-shareable parent deep links */}
                <Route path="/parent/coparent-request/:token" element={<RouteErrorBoundary><PageSuspense><CoparentRequest /></PageSuspense></RouteErrorBoundary>} />

                <Route path="/parent/time-off" element={<Navigate to="/parent?action=time-off" replace />} />
                <Route path="/parent/notifications" element={<Navigate to="/parent?action=notifications" replace />} />
                <Route path="/p/time-off" element={<Navigate to="/parent?action=time-off" replace />} />
                <Route path="/p/notify" element={<Navigate to="/parent?action=notifications" replace />} />
                <Route path="/admin" element={<PageSuspense><Admin /></PageSuspense>} />
                <Route path="/rsvp-info" element={<PageSuspense><RsvpInfo /></PageSuspense>} />
                <Route path="/rpe-info" element={<PageSuspense><RpeInfo /></PageSuspense>} />
                <Route path="/feedback-info" element={<PageSuspense><FeedbackInfo /></PageSuspense>} />
                <Route path="/fixtures-info" element={<PageSuspense><FixturesInfo /></PageSuspense>} />
                <Route path="/attendance-info" element={<PageSuspense><AttendanceInfo /></PageSuspense>} />

                {/* Localized marketing URLs for SEO + hreflang */}
                <Route path="/es" element={<Index />} />
                <Route path="/es/rsvp-info" element={<PageSuspense><RsvpInfo /></PageSuspense>} />
                <Route path="/es/rpe-info" element={<PageSuspense><RpeInfo /></PageSuspense>} />
                <Route path="/es/feedback-info" element={<PageSuspense><FeedbackInfo /></PageSuspense>} />
                <Route path="/pt-br" element={<Index />} />
                <Route path="/pt-br/rsvp-info" element={<PageSuspense><RsvpInfo /></PageSuspense>} />
                <Route path="/pt-br/rpe-info" element={<PageSuspense><RpeInfo /></PageSuspense>} />
                <Route path="/pt-br/feedback-info" element={<PageSuspense><FeedbackInfo /></PageSuspense>} />
                <Route path="/fr" element={<Index />} />
                <Route path="/fr/rsvp-info" element={<PageSuspense><RsvpInfo /></PageSuspense>} />
                <Route path="/fr/rpe-info" element={<PageSuspense><RpeInfo /></PageSuspense>} />
                <Route path="/fr/feedback-info" element={<PageSuspense><FeedbackInfo /></PageSuspense>} />
                <Route path="/ja" element={<Index />} />
                <Route path="/ja/rsvp-info" element={<PageSuspense><RsvpInfo /></PageSuspense>} />
                <Route path="/ja/rpe-info" element={<PageSuspense><RpeInfo /></PageSuspense>} />
                <Route path="/ja/feedback-info" element={<PageSuspense><FeedbackInfo /></PageSuspense>} />
                <Route path="/ja/fixtures-info" element={<PageSuspense><FixturesInfo /></PageSuspense>} />
                <Route path="/ja/attendance-info" element={<PageSuspense><AttendanceInfo /></PageSuspense>} />
                <Route path="/import-fixtures" element={<PageSuspense><ImportFixtures /></PageSuspense>} />
                
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Layout>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
